import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Tooltip } from '@factorialco/f0-react/dist/experimental'
import {
  GetAttributesFromNodeDocument,
  GetTreeNodesByTypeDocument,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  NodeAttribute,
  formatUuidToNodeType,
  getTextFromNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

import { useMoveJobCatalogTreeNode } from './useMoveJobCatalogTreeNode'

interface Props {
  nodeName: string
  onSubmit: (success: boolean) => void
  onClose: () => void
  nodeUuid?: string
  parentNodeUuid?: string
}
const MoveModal = ({ nodeName, onSubmit, onClose, nodeUuid, parentNodeUuid }: Props) => {
  const params = useParams()
  const { rootId, familyId, functionId, roleId, levelId, id } = params
  const nodeUuidFromParams = levelId || roleId || functionId || familyId || rootId || id || ''
  const parentUuid = useMemo(() => {
    if (parentNodeUuid) {
      return parentNodeUuid
    }

    return roleId && functionId
      ? functionId
      : functionId && familyId
        ? familyId
        : familyId && rootId
          ? rootId
          : ''
  }, [roleId, functionId, familyId, rootId, parentNodeUuid])

  const parentNodeType = useMemo(() => formatUuidToNodeType(parentUuid), [parentUuid])
  const parentNodeTypeLabel = useMemo(() => getTextFromNodeType(parentNodeType), [parentNodeType])

  const { data } = useQuery(GetTreeNodesByTypeDocument, {
    variables: { nodeType: parentNodeType },
    fetchPolicy: 'no-cache',
  })

  const parents = useMemo(
    () =>
      data?.jobCatalog.treeNodesConnection.nodes
        .filter((node) => node.uuid !== parentUuid)
        .map((node) => ({
          label: node.name ?? '',
          value: node.uuid,
        })) ?? [],
    [data, parentUuid]
  )

  const [newParentNodeUuid, setNewParentNodeUuid] = useState<string>('')

  useEffect(() => {
    if (parents.length === 1 && !newParentNodeUuid && parents[0]?.value) {
      setNewParentNodeUuid(parents[0].value)
    }
  }, [parents, newParentNodeUuid])

  const handleNewParentNodeUuidChange = (value: string) => {
    const actualValue = value === 'default' ? '' : value
    setNewParentNodeUuid(actualValue)
  }

  const { company } = useCurrentSession()

  const { data: parentAttributesData } = useQuery(GetAttributesFromNodeDocument, {
    variables: {
      nodeUuid: parentUuid,
    },
    fetchPolicy: 'no-cache',
    skip: !parentUuid,
  })

  const { data: newParentAttributesData } = useQuery(GetAttributesFromNodeDocument, {
    variables: {
      nodeUuid: newParentNodeUuid,
    },
    fetchPolicy: 'no-cache',
    skip: !newParentNodeUuid,
  })

  const parentAttributes = useMemo(
    () =>
      parentAttributesData?.jobCatalog.nodeAttributesConnection.nodes.filter(
        (attr) => attr.takesPrecedence
      ),
    [parentAttributesData]
  )

  const newParentAttributes = useMemo(
    () =>
      newParentAttributesData?.jobCatalog.nodeAttributesConnection.nodes.filter(
        (attr) => attr.takesPrecedence
      ),
    [newParentAttributesData]
  )

  const diffParentAttributes = useMemo(() => {
    if (!parentAttributes || !newParentAttributes) return []
    return parentAttributes.filter(
      (attribute) =>
        !newParentAttributes.some(
          (newAttribute) =>
            attribute.id === newAttribute.id ||
            (attribute.attributeId && attribute.attributeId === newAttribute.attributeId)
        )
    )
  }, [parentAttributes, newParentAttributes])

  const diffNewParentAttributes = useMemo(() => {
    if (!parentAttributes || !newParentAttributes) return []
    return newParentAttributes.filter(
      (newAttribute) =>
        !parentAttributes.some(
          (attribute) =>
            attribute.id === newAttribute.id ||
            (attribute.attributeId && attribute.attributeId === newAttribute.attributeId)
        )
    )
  }, [parentAttributes, newParentAttributes])

  const groupedParentAttributes = useMemo(
    () => groupBy(diffParentAttributes, 'type'),
    [diffParentAttributes]
  )

  const groupedNewParentAttributes = useMemo(
    () => groupBy(diffNewParentAttributes, 'type'),
    [diffNewParentAttributes]
  )

  const { moveNode } = useMoveJobCatalogTreeNode()

  const handleSubmit = async () => {
    const result = await moveNode(company.id, nodeUuid || nodeUuidFromParams, newParentNodeUuid)
    const handled = handleResult(result, (resultErrors) => {
      app.setFlash({
        type: 'wrong',
        message: resultErrors.messages || i18n.t('errors.generic_error'),
      })
    })
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.nodes.move.success'),
        time: 5000,
      })
    }
    onSubmit(handled.success)
  }

  return (
    <Modal
      illustration={'pencil'}
      title={i18n.t('job_catalog.nodes.move.title', { nodeName, nodeType: parentNodeTypeLabel })}
      onClose={onClose}
      footer={
        <Box gap="s16" flexDirection="row" alignItems="center">
          <F0Button
            label={i18n.t('job_catalog.nodes.move.cancel')}
            onClick={onClose}
            variant="outline"
          />
          <F0Button
            onClick={handleSubmit}
            disabled={!newParentNodeUuid}
            label={i18n.t('job_catalog.nodes.move.submit')}
          />
        </Box>
      }
    >
      <Box gap="s24">
        <Select
          label={parentNodeTypeLabel}
          options={parents}
          value={newParentNodeUuid || undefined}
          onChange={handleNewParentNodeUuidChange}
          loading={!data}
          defaultOption={
            newParentNodeUuid
              ? undefined
              : {
                  label: i18n.t('job_catalog.nodes.bulk_move.select_target_parent.placeholder', {
                    nodeType: parentNodeTypeLabel.toLowerCase(),
                  }),
                  disabled: true,
                  value: 'default',
                }
          }
          mandatory
        />
        {newParentNodeUuid && Object.keys(groupedParentAttributes).length > 0 && (
          <Box gap="s4">
            <Text.Normal color="textPrimary">
              {i18n.t('job_catalog.nodes.move.removes')}
            </Text.Normal>
            <Box paddingLeft="s20">
              {Object.keys(groupedParentAttributes).map((type) => {
                const attributes = groupedParentAttributes[type]
                if (!attributes) return
                return (
                  <li key={type}>
                    <Text.Normal color="textPrimary" underlined>
                      {getAttributesTranslation(type, attributes)}
                    </Text.Normal>
                  </li>
                )
              })}
            </Box>
          </Box>
        )}
        {newParentNodeUuid && Object.keys(groupedNewParentAttributes).length > 0 && (
          <Box padding="s20" gap="s4" background={'grey300'} borderRadius={{ all: 'abs008' }}>
            <Text.Normal color="textPrimary">
              {i18n.t('job_catalog.nodes.move.includes', {
                parentNode: parents.find((parent) => parent.value === newParentNodeUuid)?.label,
                node: nodeName,
              })}
            </Text.Normal>
            <Box paddingLeft="s20">
              {Object.keys(groupedNewParentAttributes).map((type) => {
                const attributes = groupedNewParentAttributes[type]
                if (!attributes) return
                return (
                  <li key={type}>
                    <Text.Normal color="textPrimary" underlined>
                      {getAttributesTranslation(type, attributes)}
                    </Text.Normal>
                  </li>
                )
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default MoveModal

const getAttributesTranslation = (attributeType: string, attributes: NodeAttribute[]) => {
  switch (attributeType) {
    case 'working_conditions':
      const [workingConditions] = attributes
      return i18n.t('job_catalog.nodes.move.attributes.working_conditions', {
        name: workingConditions?.valueWorkingConditions?.name,
      })
    case 'competency':
      return (
        <Tooltip description={attributes.map((attr) => attr.valueCompetency?.name).join(', ')}>
          <span>
            {i18n.t('job_catalog.nodes.move.attributes.competencies', { count: attributes.length })}
          </span>
        </Tooltip>
      )
    default:
      return String(attributes.length)
  }
}
